import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2, Plus, ShieldCheck, TrendingUp, Unlock, Wallet } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { stopRow, useRowDetail } from "@/components/common/DetailSheet";
import { ApprovalRow, SlabTable } from "@/components/common/Approvals";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";

const FLAG = {
  green: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  amber: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  red: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>
);

const BLANK_LINE = { scope: "fund", code: "", financial_year: "2026-27", annual_budget: 0, notes: "" };
const BLANK_REQ = { scope: "fund", code: "", amount: 0, reason: "" };

export default function Budget() {
  const { can, user } = useAuth();
  const [lines, setLines] = useState(null);
  const [requests, setRequests] = useState(null);
  const [dialog, setDialog] = useState("");
  const [line, setLine] = useState(BLANK_LINE);
  const [request, setRequest] = useState(BLANK_REQ);
  const [release, setRelease] = useState({ budget_key: "", limit: 0, rationale: "" });
  const detail = useRowDetail();
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [l, r] = await Promise.all([api.get("/budget/lines"), api.get("/budget/requests")]);
    setLines(l.data);
    setRequests(r.data);
  }, []);

  useEffect(() => { load().catch(() => setLines(false)); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      setDialog("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!lines) return <Loading label="Loading budget versus actual" />;
  const isChair = ["board_chair", "platform_admin"].includes(user?.role);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-030 · FIN-008 Budget versus actual"
        title="What you planned, what you spent, and what you have already promised"
        description="Every fund and programme is tracked against its annual budget with money already committed on open purchase orders and maintenance jobs shown separately. A line that is projected to go over stops behaving like a budget and starts behaving like a cost centre — nothing more can be spent from it until the President or Chairperson authorises the overspend, or a majority of trustees approve extra funds through the slab that fits the amount."
        actions={can("budget", "write") && (
          <div className="flex gap-2">
            <Dialog open={dialog === "req"} onOpenChange={(o) => setDialog(o ? "req" : "")}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="request-funds-button"
                        className="h-8 rounded-full border-border text-[11px]">
                  <TrendingUp className="mr-1 h-3.5 w-3.5" /> Request extra funds
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg border-border bg-card">
                <DialogHeader><DialogTitle>Ask the board for more money</DialogTitle></DialogHeader>
                <p className="text-xs text-muted-foreground">
                  The amount decides who must sign it off — these are the slabs in force.
                </p>
                <SlabTable slabs={requests?.slabs || []} />
                <div className="grid grid-cols-2 gap-3">
                  <F label="Scope">
                    <Select value={request.scope} onValueChange={(v) => setRequest({ ...request, scope: v, code: "" })}>
                      <SelectTrigger data-testid="request-scope-select" className="bg-muted/40">
                        <SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fund">Fund</SelectItem>
                        <SelectItem value="programme">Programme</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Budget line">
                    <Select value={request.code} onValueChange={(v) => setRequest({ ...request, code: v })}>
                      <SelectTrigger data-testid="request-code-select" className="bg-muted/40">
                        <SelectValue placeholder="Choose a line" /></SelectTrigger>
                      <SelectContent>
                        {lines.items.filter((i) => i.scope === request.scope).map((i) => (
                          <SelectItem key={i.code} value={i.code}>{i.code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Amount needed">
                    <Input type="number" data-testid="request-amount-input" className="bg-muted/40"
                           value={request.amount}
                           onChange={(e) => setRequest({ ...request, amount: Number(e.target.value) })} />
                  </F>
                  <div className="col-span-2"><F label="What is it for?">
                    <Textarea data-testid="request-reason-input" rows={3} className="bg-muted/40"
                              value={request.reason}
                              onChange={(e) => setRequest({ ...request, reason: e.target.value })}
                              placeholder="Cyclone Remal relief — 4,000 additional family kits and two extra field teams for six weeks" />
                  </F></div>
                </div>
                <Button data-testid="request-submit-button" disabled={busy === "req"}
                        onClick={() => act("req", () => api.post("/budget/requests", request),
                                          "Request raised and sent to the approvers")}
                        className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "req" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                  Raise the request
                </Button>
              </DialogContent>
            </Dialog>

            <Dialog open={dialog === "line"} onOpenChange={(o) => setDialog(o ? "line" : "")}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="new-budget-button"
                        className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  <Plus className="mr-1 h-3.5 w-3.5" /> New budget line
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md border-border bg-card">
                <DialogHeader><DialogTitle>Set an annual budget</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Scope">
                    <Select value={line.scope} onValueChange={(v) => setLine({ ...line, scope: v })}>
                      <SelectTrigger data-testid="budget-scope-select" className="bg-muted/40">
                        <SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fund">Fund</SelectItem>
                        <SelectItem value="programme">Programme</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Code">
                    <Input data-testid="budget-code-input" className="bg-muted/40" value={line.code}
                           onChange={(e) => setLine({ ...line, code: e.target.value.toUpperCase() })}
                           placeholder="EDU-RES" />
                  </F>
                  <F label="Financial year">
                    <Select value={line.financial_year} onValueChange={(v) => setLine({ ...line, financial_year: v })}>
                      <SelectTrigger data-testid="budget-year-select" className="bg-muted/40">
                        <SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["2025-26", "2026-27", "2027-28"].map((y) => (
                          <SelectItem key={y} value={y}>{y}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Annual budget">
                    <Input type="number" data-testid="budget-amount-input" className="bg-muted/40"
                           value={line.annual_budget}
                           onChange={(e) => setLine({ ...line, annual_budget: Number(e.target.value) })} />
                  </F>
                  <div className="col-span-2"><F label="Notes">
                    <Input data-testid="budget-notes-input" className="bg-muted/40" value={line.notes}
                           onChange={(e) => setLine({ ...line, notes: e.target.value })} />
                  </F></div>
                </div>
                <Button data-testid="budget-save-button" disabled={busy === "line"}
                        onClick={() => act("line", () => api.post("/budget/lines", line), "Budget line created")}
                        className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "line" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                  Save the line
                </Button>
              </DialogContent>
            </Dialog>
          </div>
        )}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Kpi testId="kpi-budget" label={`Budget ${lines.financial_year}`} accent="sky" icon={Wallet}
             value={money(lines.counters.budget, "INR", true)} sub={`${lines.counters.lines} lines`} />
        <Kpi testId="kpi-spent" label="Spent" accent="violet"
             value={money(lines.counters.spent, "INR", true)} />
        <Kpi testId="kpi-committed-budget" label="Committed, not yet spent" accent="amber"
             value={money(lines.counters.committed, "INR", true)} />
        <Kpi testId="kpi-amber" label="Amber lines" value={lines.counters.amber} accent="amber"
             sub={`over ${lines.thresholds.amber}% projected`} />
        <Kpi testId="kpi-red" label="Red lines" value={lines.counters.red} accent="red"
             icon={AlertTriangle} sub={`over ${lines.thresholds.red}% projected`} />
        <Kpi testId="kpi-blocked" label="Blocked as cost centre" value={lines.counters.blocked}
             accent="red" icon={ShieldCheck} />
      </div>

      <Tabs defaultValue="lines">
        <TabsList>
          <TabsTrigger value="lines" data-testid="tab-lines">Budget versus actual</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">
            Extra funds requests ({requests?.counters.pending || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lines" className="mt-4">
          <Panel testId="panel-budget-lines" kicker="FR-FIN-008"
                 title={`Fund and programme lines · ${lines.financial_year}`}>
            <Table testId="budget-table" head={<>
              <Th>Line</Th><Th>Budget</Th><Th>Spent</Th><Th>Committed</Th><Th>Remaining</Th>
              <Th>Projected</Th><Th>Status</Th><Th /></>}>
              {lines.items.map((r) => (
                <tr key={r.budget_key} className="cursor-pointer hover:bg-muted/40"
                    onClick={() => detail.show(r, r.budget_key, r.code, `${r.scope} budget line`)}
                    data-testid={`budget-row-${r.code}`}>
                  <Td>
                    <div className="font-semibold text-foreground">{r.code}</div>
                    <div className="text-[11px] capitalize text-muted-foreground">{r.scope}</div>
                  </Td>
                  <Td className="tabular-nums">
                    {money(r.ceiling ?? r.annual_budget, "INR", true)}
                    {r.ceiling && r.ceiling !== r.annual_budget && (
                      <div className="text-[10px] text-muted-foreground">
                        plan {money(r.annual_budget, "INR", true)}
                      </div>
                    )}
                  </Td>
                  <Td className="tabular-nums">{money(r.spent, "INR", true)}</Td>
                  <Td className="tabular-nums">{money(r.committed, "INR", true)}</Td>
                  <Td className={r.remaining < 0 ? "tabular-nums text-red-600 dark:text-red-400" : "tabular-nums"}>
                    {money(r.remaining, "INR", true)}
                  </Td>
                  <Td>
                    <div className="w-28">
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                        <div className={r.flag === "red" ? "h-full bg-red-500"
                          : r.flag === "amber" ? "h-full bg-amber-500" : "h-full bg-emerald-500"}
                             style={{ width: `${Math.min(100, r.projected_pct)}%` }} />
                      </div>
                      <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                        {r.projected_pct}% projected
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <Chip className={FLAG[r.flag]} testId={`flag-${r.code}`}>
                      {r.blocked ? "blocked" : r.flag === "red" ? "red" : r.flag === "amber" ? "amber" : "on plan"}
                    </Chip>
                    {r.release && (
                      <div className="mt-1 text-[10.5px] text-muted-foreground">
                        ceiling {money(r.release.limit, "INR", true)} · {r.release.approved_by}
                      </div>
                    )}
                  </Td>
                  <Td onClick={stopRow}>
                    {r.overspent && isChair && (
                      <Button size="sm" variant="outline" data-testid={`release-${r.code}`}
                              onClick={() => { setRelease({ budget_key: r.budget_key,
                                limit: Math.round((r.spent + r.committed) * 1.05), rationale: "" });
                                setDialog("release"); }}
                              className="h-7 rounded-full border-border text-[11px]">
                        <Unlock className="mr-1 h-3 w-3" /> Authorise
                      </Button>
                    )}
                    {r.overspent && !isChair && !r.release && (
                      <span className="text-[10.5px] text-muted-foreground">
                        needs the President / Chairperson
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
              {lines.items.length === 0 && <Empty colSpan={8} label="No budget lines yet" />}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="requests" className="mt-4 space-y-3">
          {(requests?.items || []).map((item) => (
            <ApprovalRow key={item.request_ref} item={item} onDone={load}
                         testId={`request-${item.request_ref}`} />
          ))}
          {(requests?.items || []).length === 0 && (
            <div className="panel p-8 text-center text-xs text-muted-foreground"
                 data-testid="empty-requests">
              No extra funds have been requested this year.
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={dialog === "release"} onOpenChange={(o) => setDialog(o ? "release" : "")}>
        <DialogContent className="max-w-md border-border bg-card">
          <DialogHeader><DialogTitle>Authorise the overspend</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            You are lifting the ceiling on <span className="font-mono">{release.budget_key}</span>.
            The line stays visible as an authorised overspend with your name against it.
          </p>
          <F label="Approved ceiling">
            <Input type="number" data-testid="release-limit-input" className="bg-muted/40"
                   value={release.limit}
                   onChange={(e) => setRelease({ ...release, limit: Number(e.target.value) })} />
          </F>
          <F label="Rationale for the board record">
            <Textarea data-testid="release-rationale-input" rows={3} className="bg-muted/40"
                      value={release.rationale}
                      onChange={(e) => setRelease({ ...release, rationale: e.target.value })}
                      placeholder="Cyclone response authorised at the emergency board call on 14 June" />
          </F>
          <Button data-testid="release-submit-button"
                  disabled={busy === "release" || release.rationale.trim().length < 15 || release.limit <= 0}
                  onClick={() => act("release", () => api.post("/budget/release", release),
                                    "Overspend authorised — the line can spend again")}
                  className="h-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "release" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Authorise as President / Chairperson
          </Button>
        </DialogContent>
      </Dialog>

      {detail.sheet}
    </div>
  );
}
